// Story 10.3 — WhatsApp-style bubble. Type-aware rendering.

import { Check, FileImage, MapPin, User } from 'lucide-react';
import type {
  ConversationMessage,
  InteractiveButtons,
  InteractiveList,
} from '@/lib/conversations-types';
import { cn } from '@/lib/utils';

interface Props {
  message: ConversationMessage;
  /** Si es outbound interactive y hubo selección, este es el id seleccionado. */
  selectedReplyId?: string;
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function ButtonsBubble({
  data,
  selectedId,
}: {
  data: InteractiveButtons;
  selectedId?: string;
}): React.ReactElement {
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {data.buttons.map((b) => {
        const selected = b.id === selectedId;
        return (
          <span
            key={b.id}
            className={cn(
              'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs',
              selected
                ? 'border-success/40 bg-success/10 text-success'
                : 'border-border/40 bg-background/30 text-current opacity-80',
            )}
          >
            {selected && <Check className="h-3 w-3" aria-hidden />}
            {b.title}
          </span>
        );
      })}
    </div>
  );
}

function ListBubble({
  data,
  selectedId,
}: {
  data: InteractiveList;
  selectedId?: string;
}): React.ReactElement {
  return (
    <div className="mt-2 space-y-2">
      {/* P-276: key compuesto por título (cuando existe) + primer-row-id +
          idx fallback. El interactive_list es inmutable una vez emitido,
          así que cualquier combinación es estable; el objetivo es no
          colisionar cuando dos secciones comparten título. */}
      {data.sections.map((section) => {
        const firstRowId = section.rows[0]?.id ?? 'empty';
        const key = `${section.title ?? 'untitled'}::${firstRowId}`;
        return (
          <div key={key}>
            {section.title && (
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                {section.title}
              </p>
            )}
            <ul className="space-y-1">
              {section.rows.map((row) => {
                const selected = row.id === selectedId;
                return (
                  <li
                    key={row.id}
                    className={cn(
                      'flex items-start gap-2 rounded-md border px-2 py-1 text-xs',
                      selected
                        ? 'border-success/40 bg-success/10 text-success'
                        : 'border-border/40 bg-background/30 opacity-80',
                    )}
                  >
                    {selected && <Check className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />}
                    <div>
                      <div className="font-medium">{row.title}</div>
                      {row.description && (
                        <div className="text-[10px] opacity-70">{row.description}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export function MessageBubble({ message, selectedReplyId }: Props): React.ReactElement {
  const isInbound = message.direction === 'inbound';
  return (
    <div
      data-direction={message.direction}
      data-message-type={message.type}
      className={cn('flex w-full', isInbound ? 'justify-start' : 'justify-end')}
    >
      <div
        className={cn(
          'max-w-[75%] rounded-2xl px-3 py-2 shadow-sm',
          isInbound
            ? 'rounded-bl-sm bg-card text-card-foreground'
            : 'rounded-br-sm bg-primary text-primary-foreground',
        )}
      >
        {message.type === 'media' && (
          <div className="mb-2 flex items-center gap-2 text-xs opacity-80">
            <FileImage className="h-4 w-4" aria-hidden />
            {message.mediaUrl ? (
              <a
                href={message.mediaUrl}
                target="_blank"
                rel="noreferrer"
                className="underline-offset-2 hover:underline"
              >
                Ver media
              </a>
            ) : (
              <span>Media</span>
            )}
          </div>
        )}
        {message.type === 'location' && (
          <div className="mb-1 flex items-center gap-1 text-xs opacity-80">
            <MapPin className="h-3 w-3" aria-hidden /> Ubicación compartida
          </div>
        )}
        {message.type === 'contact' && (
          <div className="mb-1 flex items-center gap-1 text-xs opacity-80">
            <User className="h-3 w-3" aria-hidden /> Contacto compartido
          </div>
        )}
        {message.text && <p className="whitespace-pre-wrap text-sm leading-snug">{message.text}</p>}
        {message.mediaCaption && (
          <p className="mt-1 text-xs italic opacity-80">{message.mediaCaption}</p>
        )}
        {message.interactive?.buttons && (
          <ButtonsBubble data={message.interactive.buttons} selectedId={selectedReplyId} />
        )}
        {message.interactive?.list && (
          <ListBubble data={message.interactive.list} selectedId={selectedReplyId} />
        )}
        <p
          className={cn(
            'mt-1 flex items-center justify-end gap-1 text-[10px]',
            isInbound ? 'text-muted-foreground' : 'text-primary-foreground/80',
          )}
        >
          {message.degraded && (
            <span
              data-testid="degraded-badge"
              role="note"
              aria-label="Mensaje degradado: botones convertidos a texto numerado por limitación del proveedor Evolution Go"
              title="Botones convertidos a texto numerado por limitación del proveedor Evolution Go"
              className="rounded bg-background/30 px-1 py-px text-[9px] font-medium uppercase tracking-wide opacity-80"
            >
              Modo texto
            </span>
          )}
          {formatTime(message.timestamp)}
        </p>
      </div>
    </div>
  );
}
